import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import PrintIcon from "@mui/icons-material/Print";
import { useCallback, useMemo, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  PDF_CHROME_DARK,
  PDF_CHROME_LIGHT,
  PDF_SHEET_SHADOW,
  PDF_VIEWER_MAT_BG as VIEWER_BG,
  toPdfFileData,
} from "@xop-any-time/shared";

/**
 * `PdfReader` — the ONE inline PDF reader for this portal, ported from the
 * command center (`xop-edge-staff/apps/web/src/components/pdf/pdf-reader.tsx`).
 *
 * This is the Chrome/Google-style reader (dark mat, white page sheets, a sticky
 * toolbar with zoom / fit-width / print) that every on-screen PDF renders
 * through: the CRM's invoices and quotes, the Documentation reader, and
 * customer attachments.
 *
 * ─────────────────────────────────────────────────────────────────────
 * THIS FILE WAS A STUB, and the stub was a bug.
 * ─────────────────────────────────────────────────────────────────────
 * It previously rendered a "use Download instead" card and nothing else, on the
 * reasoning that inline PDF preview was staff tooling with no counterpart here
 * and not worth a ~1 MB worker bundle. That reasoning had a hole: the CRM's
 * invoice and quote DETAIL pages are this portal's own screens, and what they
 * show IS an inline PDF — crm-api's rendering of the document, fetched as bytes
 * (`lib/documentPdf.ts`). With the stub in place a reseller opening one of its
 * own invoices saw a download prompt where the command center shows the
 * document. Restoring the real reader is what makes the two identical.
 *
 * Contract (the bug class it exists to prevent): PDFs render with
 * **pdf.js → `<canvas>`**, NEVER a `blob:`/native `<iframe>` PDF plugin. On ANY
 * failure (worker blocked by CSP, decode error, already-detached bytes) it
 * degrades to a Download-only fallback card — it can NEVER fall back to the
 * iframe. That rule was learned on the command center's Tauri shells (WebView2
 * shows a broken-document placeholder, WKWebView can hang the machine); this
 * portal is web-only, but an iframe of PDF bytes is not a thing to reintroduce
 * here either, and keeping the rule keeps the two files diffable.
 *
 * Input is raw **bytes** (`ArrayBuffer`); how those bytes are obtained is the
 * caller's job (crm-api's rendered bytes for invoices/quotes, a same-origin
 * `/file` passthrough for docs, an authenticated attachment fetch for the file
 * manager). `bytes === null` renders nothing so the caller owns its own loading
 * UI.
 *
 * Pinned by `pdf-reader.test.tsx`.
 */

// pdf.js worker from a SAME-ORIGIN bundled asset. Vite emits
// `pdf.worker.min.mjs` next to the app bundle, so it loads from the app origin
// — never a remote URL. Wrapped so a test/SSR env that can't resolve the asset
// doesn't throw at import time (react-pdf then falls back to its own default).
try {
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();
} catch {
  /* worker URL unresolvable in this environment — react-pdf uses its default */
}

// Zoom model. 100% == the comfortable base width (below), NOT necessarily the
// full column: on a very wide reader, filling the width blows a page up huge
// and hard to read. Base = min(available width, MAX_BASE_WIDTH); the page then
// floats centered on the mat (like Chrome print-preview / Google Docs). Zoom
// out shrinks it; zoom in overflows the mat and the viewer scrolls
// horizontally. The "fit width" button fills the column on demand.
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 0.2;
const GUTTER = 48; // px:3 mat padding on each side of the sheet column (2 × 24)
// Comfortable default page width (px) on wide screens — roughly a real letter
// page at a readable size, so 100% isn't a wall-filling blow-up.
const MAX_BASE_WIDTH = 1000;
// A generous band of dark mat between page sheets so every page boundary is
// unmistakable. Paired with each sheet's drop shadow, ~28px cleanly separates
// pages the way Chrome / Google Docs do.
const PAGE_GAP = "28px";

export interface PdfReaderProps {
  /** The PDF bytes to render. `null` renders NOTHING — the caller owns the
   * loading state (e.g. its own spinner while it fetches/builds the bytes). */
  bytes: ArrayBuffer | null;
  /** Used as the print document title and available to the fallback copy.
   * Defaults to a generic name so callers that don't have one still print. */
  filename?: string;
  /** Max height of the reader (default `"85vh"`). The mat scrolls within it. */
  maxHeight?: number | string;
  /** test id for the reader's outer (dark-mat) container. */
  containerTestId?: string;
  /** test id for the zoom/print toolbar. */
  toolbarTestId?: string;
  /** test id for the page-sheet column wrapper. */
  pagesTestId?: string;
  /** test id set on each individual white page sheet. */
  sheetTestId?: string;
  /** test id for the render-failure fallback card. */
  fallbackTestId?: string;
  /** Copy shown when pdf.js can't render the bytes inline. */
  fallbackText?: ReactNode;
}

export function PdfReader({
  bytes,
  filename = "document.pdf",
  maxHeight = "85vh",
  containerTestId = "pdf-reader",
  toolbarTestId = "pdf-reader-toolbar",
  pagesTestId = "pdf-reader-pages",
  sheetTestId = "pdf-page-sheet",
  fallbackTestId = "pdf-reader-fallback",
  fallbackText = "This PDF couldn’t be previewed inline. Use Download to view it.",
}: PdfReaderProps): JSX.Element | null {
  const [numPages, setNumPages] = useState(0);
  const [renderFailed, setRenderFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Fit-to-width reference: the sheet width (px) at 100% zoom — the scroll
  // area's content width minus the mat gutter. `scale` multiplies it.
  const [fitWidth, setFitWidth] = useState(0);
  const [scale, setScale] = useState(1);
  // Base width at 100% = the comfortable capped width (or the column width if
  // narrower). Zoom multiplies it.
  const baseWidth = fitWidth ? Math.min(fitWidth, MAX_BASE_WIDTH) : 0;
  const pageWidth = baseWidth ? Math.round(baseWidth * scale) : 0;

  const zoomOut = useCallback(
    () => setScale((s) => Math.max(MIN_SCALE, Math.round((s - SCALE_STEP) * 100) / 100)),
    [],
  );
  const zoomIn = useCallback(
    () => setScale((s) => Math.min(MAX_SCALE, Math.round((s + SCALE_STEP) * 100) / 100)),
    [],
  );
  // "Fit width" fills the reader column on demand: solve baseWidth × scale =
  // fitWidth, clamped to the zoom range. On a narrow column baseWidth already
  // equals fitWidth, so this is just 100%.
  const fitToWidth = useCallback(() => {
    setScale(() =>
      baseWidth
        ? Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round((fitWidth / baseWidth) * 100) / 100))
        : 1,
    );
  }, [fitWidth, baseWidth]);

  // Print like Chrome — WITHOUT re-introducing the native-PDF-plugin hang. We
  // never hand a PDF (cross-origin OR blob) to a print frame; instead we grab
  // the already-rendered pdf.js `<canvas>` pixels, drop them as full-width
  // images into a hidden same-origin iframe, and print THAT. The canvases are
  // same-origin pixel data, so `toDataURL` doesn't taint; on the off chance it
  // does, we bail silently.
  const handlePrint = useCallback(() => {
    const root = containerRef.current;
    if (!root) return;
    const canvases = Array.from(root.querySelectorAll("canvas"));
    if (canvases.length === 0) return;
    let imgs = "";
    try {
      imgs = canvases.map((c) => `<img src="${c.toDataURL("image/png")}" />`).join("");
    } catch {
      return; // tainted canvas — don't throw, just skip printing
    }
    const safeTitle = filename.replace(/[<>&"]/g, "");
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    Object.assign(iframe.style, {
      position: "fixed",
      right: "0",
      bottom: "0",
      width: "0",
      height: "0",
      border: "0",
    });
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = win?.document;
    if (!win || !doc) {
      iframe.remove();
      return;
    }
    doc.open();
    doc.write(
      `<!doctype html><html><head><title>${safeTitle}</title><style>@page{margin:12mm}html,body{margin:0}img{display:block;width:100%;page-break-after:always}</style></head><body>${imgs}</body></html>`,
    );
    doc.close();
    win.onafterprint = () => window.setTimeout(() => iframe.remove(), 500);
    // Let the images lay out before the print dialog opens.
    window.setTimeout(() => {
      win.focus();
      win.print();
    }, 300);
  }, [filename]);

  // Measure the scroll container's width so each page renders at
  // fit-to-width × zoom. A CALLBACK REF measures + observes the node the
  // instant it attaches (the scroll box only mounts once we're past the
  // early returns), so it works whether the bytes were cached or loaded cold.
  // The observer fires only on real WIDTH changes; a page's growing HEIGHT
  // never feeds back into the column width, so there's no resize loop. Guarded
  // for envs without ResizeObserver (happy-dom).
  const roRef = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    containerRef.current = node; // keep the plain ref for handlePrint()
    if (!node) return;
    const measure = (): void => setFitWidth(Math.max(1, node.clientWidth - GUTTER));
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      roRef.current = ro;
    }
  }, []);

  // Memoize the `file` prop so react-pdf doesn't reload the document on every
  // render, and hand pdf.js a COPY of the bytes via `toPdfFileData`. pdf.js
  // TRANSFERS (detaches) the buffer it's given to its worker; detaching a
  // caller's cached `bytes` directly is what threw "Buffer is already detached"
  // on a remount. A `null` here (no bytes, or bytes already detached/empty)
  // falls through to the Download card — it never throws. See
  // `@xop-any-time/shared` → `toPdfFileData`.
  const file = useMemo(() => {
    if (!bytes) return null;
    try {
      return toPdfFileData(bytes);
    } catch {
      return null;
    }
  }, [bytes]);

  if (!bytes) return null; // no PDF yet — the caller owns the loading state

  if (!file || renderFailed) {
    return (
      <Box
        data-testid={fallbackTestId}
        sx={{
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          // `action.hover` is a STANDARD MUI palette token (present in the
          // default theme too), so the fallback renders even in a context that
          // hasn't mounted the app theme — unlike the app-only `surfaceSoft`.
          bgcolor: (t) => alpha(t.palette.action.hover, 0.4),
          p: 2,
          textAlign: "center",
          fontSize: 14,
          color: "text.secondary",
        }}
      >
        {fallbackText}
      </Box>
    );
  }

  // Chrome-style reader: a sticky dark toolbar (zoom + print) over a dark mat
  // that scrolls the white page sheets. Sheets render at `pageWidth` (fit-width
  // × zoom); above 100% they overflow the mat and the mat scrolls horizontally.
  const iconSx = {
    color: alpha(PDF_CHROME_LIGHT, 0.85),
    "&.Mui-disabled": { color: alpha(PDF_CHROME_LIGHT, 0.25) },
  };
  return (
    <Box
      data-testid={containerTestId}
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: VIEWER_BG,
      }}
    >
      {/* Toolbar — the Chrome/Google control strip. Download lives on the page
        * around this reader; here we own zoom + print of the on-screen doc. */}
      <Box
        data-testid={toolbarTestId}
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
          px: 1,
          py: 0.5,
          color: PDF_CHROME_LIGHT,
          bgcolor: alpha(PDF_CHROME_DARK, 0.35),
          borderBottom: 1,
          borderColor: alpha(PDF_CHROME_LIGHT, 0.12),
        }}
      >
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              size="small"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              aria-label="Zoom out"
              sx={iconSx}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Box
          sx={{
            minWidth: 46,
            textAlign: "center",
            fontSize: 12,
            fontVariantNumeric: "tabular-nums",
            userSelect: "none",
          }}
        >
          {Math.round(scale * 100)}%
        </Box>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              size="small"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              aria-label="Zoom in"
              sx={iconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit width">
          <IconButton
            size="small"
            onClick={fitToWidth}
            aria-label="Fit width"
            sx={{ ...iconSx, ml: 0.25 }}
          >
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box
          sx={{
            width: "1px",
            alignSelf: "stretch",
            my: 0.5,
            mx: 0.75,
            bgcolor: alpha(PDF_CHROME_LIGHT, 0.15),
          }}
        />
        <Tooltip title="Print">
          <IconButton size="small" onClick={handlePrint} aria-label="Print" sx={iconSx}>
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Scroll area (the dark mat). `measureRef` measures THIS box, so the
        * fit-width reference is the real available width; `scrollbarGutter`
        * keeps that stable when the vertical scrollbar toggles. */}
      <Box
        ref={measureRef}
        sx={{
          flex: 1,
          overflow: "auto",
          scrollbarGutter: "stable",
          "& canvas": { display: "block" },
        }}
      >
        {/* Sheet column. NOTE: react-pdf wraps the pages in its OWN
          * `.react-pdf__Document` div, so a `gap` here would only space that
          * single wrapper — the pages are grandchildren and would sit flush
          * (the "no gap between pages" bug). The gap therefore lives as a
          * `marginBottom` on each page sheet below. This wrapper just supplies
          * the mat padding and keeps sheets centered. */}
        <Box data-testid={pagesTestId} sx={{ minWidth: "100%", px: 3, py: 3 }}>
          <Document
            file={file}
            onLoadSuccess={(doc: { numPages: number }) => setNumPages(doc.numPages)}
            onLoadError={() => setRenderFailed(true)}
            loading={
              <Typography sx={{ fontSize: 13, color: alpha(PDF_CHROME_LIGHT, 0.6) }}>
                Loading PDF…
              </Typography>
            }
            error={null}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <Box
                key={i}
                data-testid={sheetTestId}
                sx={{
                  ...(pageWidth ? { width: pageWidth } : {}),
                  mx: "auto", // center the sheet on the mat (collapses when zoomed past the mat → scrolls)
                  mb: PAGE_GAP, // the dark-mat band BETWEEN pages (works despite react-pdf's wrapper div)
                  // A soft drop shadow so each white sheet lifts off the dark
                  // mat — the signature Chrome/Google PDF-reader look. Shared
                  // raw shadow (not `theme.shadows`, tuned for light surfaces).
                  boxShadow: PDF_SHEET_SHADOW,
                  borderRadius: 0.5,
                  overflow: "hidden",
                  bgcolor: PDF_CHROME_LIGHT,
                }}
              >
                <Page
                  pageNumber={i + 1}
                  {...(pageWidth ? { width: pageWidth } : {})}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Box>
            ))}
          </Document>
        </Box>
      </Box>
    </Box>
  );
}

export default PdfReader;
